import React, { useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import Toaster from "../utils/Toaster";
import { Box } from "@mui/material";
import { useTheme } from "../../theme/ThemeContext";
import AppButton from "../common/Button";
import { Container } from "../common/Container";
import { AppTypography } from "../common/Typography";
import Card from "../common/Card";
import { EmailInput } from "../common/Input";
import { usePreserveStoreIdNavigate } from "../utils/usePreserveStoreIdNavigate";

const ForgotPassword = () => {
  const [formData, setFormData] = useState({ email: "" });
  const [loading, setLoading] = useState(false);
  const navigate = usePreserveStoreIdNavigate();
  const location = useLocation();
  const theme = useTheme();
  const handleChange = (evt) => {
    const name = evt.target.name;
    const value = evt.target.value;
    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    if (!formData.email) {
      Toaster.show("Please enter your email address", "warning");
      return false;
    }

    if (!formData.email.includes("@")) {
      Toaster.show("Please enter a valid email address", "warning");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Simulate API call for password reset
      await new Promise((resolve) => setTimeout(resolve, 2000));

      Toaster.show(
        "Password reset link sent to your email! Please check your inbox.",
        "success"
      );

      // Redirect to password reset page with email as parameter
      setTimeout(() => {
        navigate(
          `/reset-password?email=${encodeURIComponent(formData.email)}`,
          {
            replace: true,
          }
        );
      }, 1500);
    } catch (error) {
      Toaster.show("Failed to send reset link. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...theme?.background,
        flex: 1,
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
          }}
        >
          {/* Logo/Brand Section */}
          <Box
            sx={{
              textAlign: "center",
              mb: 2,
            }}
          >
            <AppTypography
              variant="h3"
              style={theme?.typography?.heading}
              fontWeight={700}
              fontSize="2.5rem"
              textAlign="center"
              mb={1}
            >
              Forgot Password
            </AppTypography>
            <AppTypography
              variant="body1"
              style={theme?.typography?.body}
              fontSize="1.1rem"
              textAlign="center"
              sx={{ opacity: 0.9 }}
            >
              Enter your email to receive a password reset link
            </AppTypography>
          </Box>

          {/* Forgot Password Card */}
          <Card
            variant="elevated"
            sx={{
              width: "100%",
              maxWidth: 400,
              p: 4,
              borderRadius: 2,
              ...theme?.layout_styles?.paper,
            }}
          >
            <Box
              component="form"
              onSubmit={handleSubmit}
              autoComplete="on"
              noValidate
              sx={{ display: "flex", flexDirection: "column", gap: 3 }}
            >
              {/* Email Input */}
              <EmailInput
                name="email"
                value={formData.email}
                onChange={handleChange}
                autoComplete="email"
                required
                size="large"
                placeholder="Enter your email address"
              />

              {/* Submit Button */}
              <AppButton
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.5,
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  borderRadius: 2,
                  ...theme?.button_styles?.app,
                }}
              >
                {loading ? "Sending Reset Link..." : "Send Reset Link"}
              </AppButton>

              {/* Divider */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    height: 1,
                    ...theme?.divider,
                  }}
                />
                <AppTypography
                  variant="body2"
                  style={theme?.typography?.body}
                  fontSize="0.875rem"
                  textAlign="center"
                  sx={{ opacity: 0.7 }}
                >
                  or
                </AppTypography>
                <Box
                  sx={{
                    flex: 1,
                    height: 1,
                    ...theme?.divider,
                  }}
                />
              </Box>

              {/* Back to Login Link */}
              <Box sx={{ textAlign: "center" }}>
                <AppTypography
                  variant="body2"
                  style={theme?.typography?.body}
                  fontSize="0.875rem"
                  textAlign="center"
                  sx={{ opacity: 0.7 }}
                >
                  Remember your password?{" "}
                  <AppTypography
                    component={RouterLink}
                    to={`/login${location.search || ""}`}
                    style={theme?.typography?.link}
                    fontSize="0.875rem"
                    fontWeight={600}
                    sx={{
                      textDecoration: "none",
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Sign in here
                  </AppTypography>
                </AppTypography>
              </Box>

              {/* Register Link */}
              <Box sx={{ textAlign: "center" }}>
                <AppTypography
                  variant="body2"
                  style={theme?.typography?.body}
                  fontSize="0.875rem"
                  textAlign="center"
                  sx={{ opacity: 0.7 }}
                >
                  Don't have an account?{" "}
                  <AppTypography
                    component={RouterLink}
                    to={`/register${location.search || ""}`}
                    style={theme?.typography?.link}
                    fontSize="0.875rem"
                    fontWeight={600}
                    sx={{
                      textDecoration: "none",
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Create one here
                  </AppTypography>
                </AppTypography>
              </Box>
            </Box>
          </Card>
        </Box>
      </Container>
    </Box>
  );
};

export default ForgotPassword;
